import {
  AssetNotificationAlertLevel,
  AssetNotificationSettingStatus,
  AssetNotificationType,
  AssetType,
  DiseaseType as Disease_Type,
  IndustryType,
  getListAssetsAssetsGetQueryKey,
  type AssetNotificationSettingSchema as AssetNotificationSetting,
  type UpdateNotificationSettingsRequest,
} from "@phc/common/gen/preferencesGen"
import { useQueryClient } from "@tanstack/react-query"

import { captureMessage } from "@sentry/react"
import { isEqual } from "lodash-es"
import { enqueueSnackbar } from "notistack"
import type React from "react"
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react"
import { SeverityLevel } from "../../../../utils/constants"
import { getGlobalAsset } from "../../../../utils/helpers/assetHelper"
import { trackEvent } from "../../../../utils/mixpanel"
import type { Asset, AssetInput } from "../../../../utils/preferencesApi"
import { useCreateAsset } from "../../../WatchedLocations/AssetManagement/hooks/useCreateAsset"
import { useListAssets } from "../../../WatchedLocations/hooks/useAssetService"
import { useUpdateNotificationSettings } from "../hooks/useUpdateNotificationSettings"

export const EXPERT_INSIGHT_TYPES: AssetNotificationType[] = [
  AssetNotificationType.ASSET_NOTIFICATION_TYPE_ALERT,
  AssetNotificationType.ASSET_NOTIFICATION_TYPE_ANALYSIS,
]

export enum SettingGranularity {
  GLOBAL = "global",
  ASSET = "asset",
}

const GLOBAL_ASSET_NAME = "PLANET_EARTH"

const NotificationContext = createContext<{
  assets?: Asset[]
  assetsLoading: boolean
  submitNotificationSettings: () => Promise<void>
  discardNotificationSettings: () => void
  analysisEnabled: boolean
  setAnalysisEnabled: React.Dispatch<React.SetStateAction<boolean>>
  alertsEnabled: boolean
  setAlertsEnabled: React.Dispatch<React.SetStateAction<boolean>>
  alert_level: AssetNotificationAlertLevel
  setAlertLevel: React.Dispatch<
    React.SetStateAction<AssetNotificationAlertLevel>
  >
  hasChangedSettings: boolean
  isSaving: boolean
} | null>(null)

export const NotificationContextProvider: React.FC<{
  assets?: Asset[]
  children?: React.ReactNode
}> = ({ assets, children }) => {
  const queryClient = useQueryClient()

  const [isSaving, setIsSaving] = useState(false)
  const { mutateAsync: updateSettings } = useUpdateNotificationSettings()

  const { mutateAsync: createAsset } = useCreateAsset({ hideToast: true })
  const { isRefetching, isLoading } = useListAssets({
    includeGlobal: true,
  })

  const globalAsset = useMemo(() => getGlobalAsset(assets), [assets])

  const globalAlertsEnabledSetting = useMemo(
    () =>
      getGlobalEnabledSettingOfType(
        AssetNotificationType.ASSET_NOTIFICATION_TYPE_ALERT,
        assets
      ),
    [assets]
  )

  const globalAnalysisEnabledSetting = useMemo(
    () =>
      getGlobalEnabledSettingOfType(
        AssetNotificationType.ASSET_NOTIFICATION_TYPE_ANALYSIS,
        assets
      ),
    [assets]
  )

  const globalAlertSettingLevel = useMemo(
    () =>
      globalAlertsEnabledSetting
        ? alertSettingLevel([globalAlertsEnabledSetting])
        : undefined,
    [globalAlertsEnabledSetting]
  )

  const [analysisEnabled, setAnalysisEnabled] = useState(
    !!globalAnalysisEnabledSetting
  )
  const [alertsEnabled, setAlertsEnabled] = useState(
    !!globalAlertsEnabledSetting
  )
  const [alert_level, setAlertLevel] = useState<AssetNotificationAlertLevel>(
    globalAlertSettingLevel ??
      AssetNotificationAlertLevel.ASSET_NOTIFICATION_ALERT_LEVEL_ADVISORY
  )

  const discardNotificationSettings = useCallback(() => {
    setAnalysisEnabled(!!globalAnalysisEnabledSetting)
    setAlertsEnabled(!!globalAlertsEnabledSetting)
    setAlertLevel(
      globalAlertSettingLevel ??
        AssetNotificationAlertLevel.ASSET_NOTIFICATION_ALERT_LEVEL_ADVISORY
    )
    trackEvent("DISCARD_NOTIFICATIONS")
  }, [
    globalAnalysisEnabledSetting,
    globalAlertsEnabledSetting,
    globalAlertSettingLevel,
  ])

  const updatedGlobalNotificationSettings:
    | UpdateNotificationSettingsRequest
    | undefined = useMemo(() => {
    if (!globalAsset) return undefined
    return {
      asset_notification_settings: buildUpdatedAssetWithSettings({
        asset_id: globalAsset.asset_id ?? "",
        alertsEnabled,
        alert_level,
        analysisEnabled,
      }),
    }
  }, [globalAsset, alertsEnabled, alert_level, analysisEnabled])

  const hasChangedSettings = useMemo(() => {
    if (isSaving || isLoading) {
      return false
    }

    if (!globalAsset) return true

    if (!updatedGlobalNotificationSettings) {
      return false
    }

    const settingsMatchOriginalState =
      updatedGlobalNotificationSettings.asset_notification_settings.every(
        updatedGlobalSetting => {
          const matchingGlobalAssetSetting =
            globalAsset.notification_settings?.find(
              globalSetting =>
                globalSetting.notification_type ===
                  updatedGlobalSetting.notification_type &&
                globalSetting.disease_type ===
                  updatedGlobalSetting.disease_type &&
                globalSetting.disease_type === updatedGlobalSetting.disease_type
            )

          return matchingGlobalAssetSetting
            ? isEqual(matchingGlobalAssetSetting, updatedGlobalSetting)
            : false
        }
      )

    return !settingsMatchOriginalState
  }, [globalAsset, isLoading, isSaving, updatedGlobalNotificationSettings])

  const trackNotificationsUpdate = useCallback(() => {
    trackEvent("SAVE_NOTIFICATIONS", {
      analysisEnabled,
      alertsEnabled,
      alert_level: AssetNotificationAlertLevel[alert_level],
    })
  }, [alert_level, alertsEnabled, analysisEnabled])

  const submitNotificationSettings = useCallback(async () => {
    setIsSaving(true)

    if (!globalAsset) {
      const newAsset = createGlobalAsset()
      newAsset.notification_settings = buildUpdatedAssetWithSettings({
        asset_id: "",
        alertsEnabled,
        alert_level,
        analysisEnabled,
      })
      await createAsset({ asset: newAsset })
    } else if (updatedGlobalNotificationSettings) {
      await updateSettings({
        asset_notification_settings:
          updatedGlobalNotificationSettings.asset_notification_settings,
      })
    }

    await queryClient.invalidateQueries({
      queryKey: getListAssetsAssetsGetQueryKey(),
    })
    await queryClient.invalidateQueries({
      predicate: query =>
        typeof query.queryKey[0] === "string" &&
        query.queryKey[0].startsWith("/asset/"),
    })

    setIsSaving(false)
    trackNotificationsUpdate()
    enqueueSnackbar("Your notification settings have been updated.", {
      variant: "success",
    })
  }, [
    alert_level,
    alertsEnabled,
    analysisEnabled,
    createAsset,
    globalAsset,
    queryClient,
    trackNotificationsUpdate,
    updateSettings,
    updatedGlobalNotificationSettings,
  ])

  const context = useMemo(
    () => ({
      assets,
      assetsLoading: isRefetching || isLoading,
      submitNotificationSettings,
      discardNotificationSettings,
      analysisEnabled,
      setAnalysisEnabled,
      alertsEnabled,
      setAlertsEnabled,
      alert_level,
      setAlertLevel,
      hasChangedSettings,
      isSaving,
    }),
    [
      assets,
      isRefetching,
      isLoading,
      submitNotificationSettings,
      discardNotificationSettings,
      analysisEnabled,
      alertsEnabled,
      alert_level,
      hasChangedSettings,
      isSaving,
    ]
  )

  return (
    <NotificationContext.Provider value={context}>
      {children}
    </NotificationContext.Provider>
  )
}

export function useNotificationContext() {
  const context = useContext(NotificationContext)

  if (context == null) {
    captureMessage(
      "useNotificationContext must be used within a NotificationContext",
      SeverityLevel.Fatal
    )
    throw new Error(
      "useNotificationContext must be used within a NotificationContext"
    )
  }

  return useMemo(
    () => ({
      assets: context.assets,
      assetsLoading: context.assetsLoading,
      hasChangedSettings: context.hasChangedSettings,
      isSaving: context.isSaving,
      analysisEnabled: context.analysisEnabled,
      setAnalysisEnabled: context.setAnalysisEnabled,
      alertsEnabled: context.alertsEnabled,
      setAlertsEnabled: context.setAlertsEnabled,
      alert_level: context.alert_level,
      setAlertLevel: context.setAlertLevel,
      submitNotificationSettings: context.submitNotificationSettings,
      discardNotificationSettings: context.discardNotificationSettings,
    }),
    [context]
  )
}

function buildNotificationSetting(
  asset_id: string,
  settingType: AssetNotificationType,
  disease_type: Disease_Type,
  status: AssetNotificationSettingStatus,
  alert_level?: AssetNotificationAlertLevel
): AssetNotificationSetting {
  const setting: AssetNotificationSetting = {
    asset_id,
    notification_type: settingType,
    disease_type,
    status,
  }

  if (
    settingType === AssetNotificationType.ASSET_NOTIFICATION_TYPE_ALERT &&
    alert_level != null
  ) {
    setting.alert_level = alert_level
  }

  return setting
}

export function getGlobalEnabledSettingOfType(
  notification_type: AssetNotificationType,
  assets?: Asset[]
) {
  return assets?.find(
    asset =>
      hasEnabledSettingOfType(notification_type, asset) &&
      asset.name === GLOBAL_ASSET_NAME
  )
}

function hasEnabledSettingOfType(
  notification_type: AssetNotificationType,
  asset?: Asset
) {
  return !!asset?.notification_settings?.find(
    setting =>
      setting.notification_type === notification_type &&
      setting.status ===
        AssetNotificationSettingStatus.ASSET_NOTIFICATION_SETTING_STATUS_ENABLED
  )
}

export function alertSettingLevel(assets?: Asset[]) {
  return assets?.flatMap(asset => {
    const alert_level = asset.notification_settings?.find(
      setting =>
        setting.notification_type ===
          AssetNotificationType.ASSET_NOTIFICATION_TYPE_ALERT &&
        setting.status ===
          AssetNotificationSettingStatus.ASSET_NOTIFICATION_SETTING_STATUS_ENABLED
    )
    return alert_level?.alert_level ?? []
  })[0]
}

export const buildUpdatedAssetWithSettings = ({
  asset_id,
  alertsEnabled,
  alert_level,
  analysisEnabled,
}: {
  asset_id: string
  alertsEnabled: boolean
  alert_level: AssetNotificationAlertLevel
  analysisEnabled: boolean
}): AssetNotificationSetting[] => {
  const updatedSettings: AssetNotificationSetting[] = []

  Object.values(Disease_Type).forEach(diseaseType => {
    if (diseaseType === Disease_Type.TYPE_UNSPECIFIED) return

    updatedSettings.push(
      buildNotificationSetting(
        asset_id,
        AssetNotificationType.ASSET_NOTIFICATION_TYPE_ALERT,
        diseaseType,
        alertsEnabled
          ? AssetNotificationSettingStatus.ASSET_NOTIFICATION_SETTING_STATUS_ENABLED
          : AssetNotificationSettingStatus.ASSET_NOTIFICATION_SETTING_STATUS_DISABLED,
        alert_level
      )
    )

    updatedSettings.push(
      buildNotificationSetting(
        asset_id,
        AssetNotificationType.ASSET_NOTIFICATION_TYPE_ANALYSIS,
        diseaseType,
        analysisEnabled
          ? AssetNotificationSettingStatus.ASSET_NOTIFICATION_SETTING_STATUS_ENABLED
          : AssetNotificationSettingStatus.ASSET_NOTIFICATION_SETTING_STATUS_DISABLED,
        undefined
      )
    )
  })

  return updatedSettings
}

const createGlobalAsset = (): AssetInput => {
  return {
    asset_id: "",
    name: GLOBAL_ASSET_NAME,
    threat_radius: 12500,
    industry: IndustryType.INDUSTRY_TYPE_UNSPECIFIED,
    notes: "",
    asset_types: [AssetType.ASSET_TYPE_UNSPECIFIED],
    asset_groups: [],
    notification_settings: [],
    base_event: {
      geotags: [
        {
          location_id: GLOBAL_ASSET_NAME,
          wkt_centroid: "POINT (0.0 0.0)",
        },
      ],
    },
  }
}
